import { Security } from "@okta/okta-react";
import type { JSX } from "react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { RouterProvider, createBrowserRouter } from "react-router-dom";

import "./windowConfiguration.ts";

import "@/i18n/initI18n.ts";
import { LocalizationProvider } from "@/providers";
import { store } from "@/store/store.ts";
import { ThemeProviderWrapper } from "@/theme/ThemeProvider.tsx";

import { LoginCallback } from "./components/LoginCallback/index.ts";
import { oktaAuth } from "./oktaAuth.ts";
import { routes } from "./routes/routes";
import "./styles/global.css";
import { trackPageView } from "./utils/tracking/usage-tracking.ts";

const App = (): JSX.Element => {
  const router = createBrowserRouter([
    ...routes,
    {
      path: "/login/callback",
      element: <LoginCallback />,
    },
  ]);

  const restoreOriginalUri = async (): Promise<void> => {};

  return (
    <StrictMode>
      <Provider store={store}>
        <ThemeProviderWrapper>
          <LocalizationProvider>
            <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
              <RouterProvider router={router} />
            </Security>
          </LocalizationProvider>
        </ThemeProviderWrapper>
      </Provider>
    </StrictMode>
  );
};

export default App;

const deprecatedDomains = [
  "app.dev.indexportfolio.sprengnetter.de",
  "app.test.indexportfolio.sprengnetter.de",
  "app.indexportfolio.sprengnetter.de",
];

const isDeprecatedDomain = deprecatedDomains.includes(window.location.hostname);

const setupTracking = (scriptSrc: string): void => {
  if (!document.getElementById("ets-script")) {
    const etsScript = document.createElement("script");
    etsScript.id = "ets-script";
    etsScript.src = scriptSrc;
    etsScript.async = true;
    document.body.appendChild(etsScript);
  }
  window.fireReporting = (): void => {
    trackPageView();
  };
  window.fireReporting();
};

if (!isDeprecatedDomain) {
  if (window.isDevelopment() || window.isTest()) {
    setupTracking(
      "https://tracking.sandbox-immobilienscout24.de/immobilienscout/is24-de-satellite/tr.js?cP--countername=document.location.pathname&cP--svc_module_name=valuationhub&addOnClick&initialPageView=false",
    );
  } else if (window.isProduction()) {
    setupTracking(
      "https://tracking.immobilienscout24.de/immobilienscout/is24-de-satellite/tr.js?cP--countername=sn.de.document.location.pathname&cP--svc_module_name=valuationhub&addOnClick&initialPageView=false",
    );
  }
}

createRoot(document.getElementById("root")!).render(<App />);
